import { getBackendAPIClient } from "@lib/backendAPIClient";
import { billingTransferContract } from "@common/contracts/billing-transfer.contract";
import { ServerInferRequest } from "@ts-rest/core";
import {
    useMutation,
    UseMutationOptions,
    useQuery,
} from "@tanstack/react-query";
import { queryClient } from "queryClient";
import { workspaceQueryKeys } from "./workspace.api";

const billingTransferContractClient = getBackendAPIClient(
    billingTransferContract,
);

const billingTransferQueryKeys = {
    all: () => ["billing-transfer"],
    getTransferRequestByCode: (code: string) => [
        ...billingTransferQueryKeys.all(),
        "getTransferRequestByCode",
        code,
    ],
    pendingTransferRequests: () => [
        ...billingTransferQueryKeys.all(),
        "pendingTransferRequests",
    ],
    pendingTransferRequestsForWorkspace: (workspaceId: string) => [
        ...billingTransferQueryKeys.pendingTransferRequests(),
        workspaceId,
    ],
};

export function useGetTransferRequestByCodeQuery(code: string) {
    return useQuery({
        queryKey: billingTransferQueryKeys.getTransferRequestByCode(code),
        queryFn: async () => {
            const response =
                await billingTransferContractClient.getTransferRequestByCode({
                    params: { requestCode: code },
                });
            return response.body;
        },
    });
}

type TransferBillingToClientPayload = ServerInferRequest<
    typeof billingTransferContract.transferToClient
>["body"];

export function useTransferBillingToClientMutation() {
    return useMutation({
        mutationFn: async (params: TransferBillingToClientPayload) => {
            await billingTransferContractClient.transferToClient({
                body: params,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}

type RequestBillingTransferFromClientPayload = ServerInferRequest<
    typeof billingTransferContract.requestBillingTransferFromClient
>["body"];

export function useRequestBillingTransferFromClientMutation() {
    return useMutation({
        mutationFn: async (params: RequestBillingTransferFromClientPayload) => {
            await billingTransferContractClient.requestBillingTransferFromClient(
                {
                    body: params,
                },
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}

type RequestBillingTransferFromOrganizationPayload = ServerInferRequest<
    typeof billingTransferContract.requestBillingTransferFromOrganization
>["body"];

export function useRequestBillingTransferFromOrganizationMutation() {
    return useMutation({
        mutationFn: async (
            params: RequestBillingTransferFromOrganizationPayload,
        ) => {
            await billingTransferContractClient.requestBillingTransferFromOrganization(
                {
                    body: params,
                },
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: billingTransferQueryKeys.pendingTransferRequests(),
            });
        },
    });
}

type ResolveBillingTransferRequestPayload = ServerInferRequest<
    typeof billingTransferContract.resolveBillingTransferRequest
>["body"];

export function useResolveBillingTransferRequestMutation(
    options?: Pick<
        UseMutationOptions<void, Error, ResolveBillingTransferRequestPayload>,
        "onSuccess"
    >,
) {
    return useMutation({
        mutationFn: async (params: ResolveBillingTransferRequestPayload) => {
            await billingTransferContractClient.resolveBillingTransferRequest({
                body: params,
            });
        },
        ...options,
    });
}

export function useCancelBillingTransferRequestMutation() {
    return useMutation({
        mutationFn: async (requestId: string) => {
            await billingTransferContractClient.cancelBillingTransferRequest({
                params: { requestId },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: billingTransferQueryKeys.pendingTransferRequests(),
            });

            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}

export function usePendingTransferRequestsQuery(workspaceId: string) {
    return useQuery({
        queryKey:
            billingTransferQueryKeys.pendingTransferRequestsForWorkspace(
                workspaceId,
            ),
        queryFn: async () => {
            const response =
                await billingTransferContractClient.getPendingTransferRequestsForWorkspace(
                    {
                        params: { workspaceId },
                    },
                );
            return response.body;
        },
    });
}
